import {
  forwardRef,
  memo,
  Ref,
  useCallback,
  useEffect,
  useRef,
  useState,
  ReactNode,
  PropsWithChildren,
} from 'react';
import { Column } from '~/commonComponents/Column';
import { RadioOption } from '~/commonComponents/RadioOption';
import { Field, IFieldProps } from './Field';
import { IInputJobProps, TOptionValue } from './Types';

type TPrimitiveValue = string | number | boolean;

interface IRadioProps<T extends TPrimitiveValue> extends IInputJobProps<T> {
  autoClose?: boolean;
  /** Icon source for the field */
  IconSrc: IFieldProps['IconSrc'];
  /** Array of options to display */
  options: TOptionValue<T>[];
  /** Title for the field */
  title: string;
  /** text attribute prefix for testing */
  testName: string;
  /** Custom value component that receives the current value */
  ValueComponent?: (currentValue: T | undefined) => ReactNode;
}

const Component = <T extends TPrimitiveValue>(
  {
    IconSrc,
    ValueComponent,
    autoClose = true,
    children,
    defaultValue,
    isDisabled = false,
    isOpen = false,
    value: controlledValue,
    onChange,
    onToggle,
    options,
    title,
    testName,
  }: PropsWithChildren<IRadioProps<T>>,
  ref: Ref<HTMLDivElement>,
) => {
  const [internalValue, setInternalValue] = useState(defaultValue);

  const isControlled = controlledValue !== undefined;
  const currentValue = isControlled ? controlledValue : internalValue;

  const wasControlledRef = useRef(isControlled);
  useEffect(() => {
    if (!import.meta.env.PROD && wasControlledRef.current !== isControlled) {
      console.warn(
        `Warning: Radio component is changing from ${wasControlledRef.current ? 'controlled' : 'uncontrolled'} to ${isControlled ? 'controlled' : 'uncontrolled'}. ` +
          'Elements should not switch from controlled to uncontrolled (or vice versa).',
      );
    }
    wasControlledRef.current = isControlled;
  }, [isControlled]);

  const doUpdateValue = useCallback(
    (newValue: T) => {
      if (!isControlled) {
        setInternalValue(newValue);
      }
      onChange?.(newValue);
    },
    [isControlled, onChange],
  );

  const handleValueChange = useCallback(
    (value: T) => {
      if (autoClose) {
        onToggle?.();
      }
      doUpdateValue(value);
    },
    [autoClose, doUpdateValue, onToggle],
  );

  const CustomValueComponent = useCallback(
    () => (ValueComponent ? ValueComponent(currentValue) : null),
    [ValueComponent, currentValue],
  );

  return (
    <Field
      ComponentProps={{
        Button: {
          'data-cy': `${testName}.trigger`,
          'data-testid': `${testName}.trigger`,
        },
        Container: { 'data-cy': testName, 'data-testid': testName },
      }}
      IconSrc={IconSrc}
      ValueComponent={CustomValueComponent}
      isDisabled={isDisabled}
      isOpen={isOpen}
      ref={ref}
      title={title}
      value={ValueComponent ? undefined : String(currentValue)}
      onToggle={onToggle}
    >
      <Column alignItems="flex-end" paddingRight={68}>
        <Column gap={10}>
          {options.map((option) => (
            <RadioOption
              checked={option.value === currentValue}
              dataCy={`${testName}.${option.value}`}
              key={String(option.value)}
              text={option.label}
              onClick={() => handleValueChange(option.value)}
            />
          ))}
        </Column>
        {children}
      </Column>
    </Field>
  );
};

/**
 * A generic radio selection component for InputJob forms.
 *
 * This component provides a reusable radio button interface that can handle
 * any primitive value type (string, number, boolean). It manages both controlled
 * and uncontrolled modes, provides consistent styling and behavior across all
 * InputJob components, and supports custom value rendering through ValueComponent.
 *
 * Features:
 * - Supports both controlled and uncontrolled modes with automatic state management
 * - Generic type support for string, number, and boolean values
 * - Custom value display through ValueComponent prop
 * - Automatic collapse after selection with configurable delay
 * - Development mode warnings for control mode switching
 * - Comprehensive accessibility and testing attributes
 * - Memory leak prevention with proper timeout cleanup
 *
 * @example
 * ```tsx
 * // Basic usage with string options
 * <Radio
 *   options={[
 *     { label: 'Option 1', value: 'opt1' },
 *     { label: 'Option 2', value: 'opt2' }
 *   ]}
 *   IconSrc={MyIcon}
 *   title="Select Option:"
 *   testName="my-radio"
 *   value={selectedValue}
 *   onChange={setSelectedValue}
 * />
 *
 * // With custom value display
 * <Radio
 *   options={booleanOptions}
 *   IconSrc={YesNoIcon}
 *   title="Enable Feature:"
 *   testName="feature-toggle"
 *   ValueComponent={(value) => (
 *     <CustomDisplay value={value} isOpen={isOpen} />
 *   )}
 *   value={isEnabled}
 *   onChange={setIsEnabled}
 * />
 *
 * // Uncontrolled mode
 * <Radio
 *   options={options}
 *   IconSrc={Icon}
 *   title="Default Selection:"
 *   testName="default-radio"
 *   defaultValue="defaultOption"
 *   onChange={handleChange}
 * />
 * ```
 *
 * @template T - The primitive value type (string | number | boolean)
 * @param props - Component props extending IInputJobProps
 * @param props.IconSrc - Icon source for the field header
 * @param props.options - Array of radio options with label and value
 * @param props.title - Display title for the radio field
 * @param props.testName - Prefix for data-cy and data-testid attributes
 * @param props.ValueComponent - Optional custom component for value display
 * @param props.defaultValue - Default selected value (uncontrolled mode)
 * @param props.value - Current selected value (controlled mode)
 * @param props.onChange - Callback fired when selection changes
 * @param props.isDisabled - Whether the component is disabled
 * @param props.isOpen - Whether the dropdown is open
 * @param props.onToggle - Callback fired when dropdown is toggled
 * @param ref - React ref forwarded to the root element
 *
 * @component
 */
export const Radio = memo(forwardRef(Component)) as <T extends TPrimitiveValue>(
  props: PropsWithChildren<IRadioProps<T>> & { ref?: Ref<HTMLDivElement> },
) => ReturnType<typeof Component>;
